import React, { useState, useEffect, useCallback } from 'react';
import { preJoinerApi, docReqApi, documentApi } from '../lib/api';
import { signatureApi } from '../lib/api';
import SignatureApprovalDialog from '../components/SignatureApprovalDialog';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';

export default function PreJoining() {
  const signTokenRef = React.useRef('');
  const [signAsk, setSignAsk] = useState(null);   // { sentTo, pending }
  const { activeUnit } = useUnit();
  const { user } = useAuth();
  // Clearing a pre-joining record is an administrator action — the backend
  // enforces the same rule, this only decides whether the control is offered.
  const isSuperAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [refuseFor, setRefuseFor] = useState(null);
  const [reason, setReason] = useState('');
  const [joinFor, setJoinFor] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await preJoinerApi.list({ unit: activeUnit }); setRows(r.data || []); }
    catch (e) { toast.error('Failed to load pre-joiners'); }
  }, [activeUnit]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { docReqApi.list().then((r) => setReqs(r.data || [])).catch(() => setReqs([])); }, []);

  const removeEntry = async (p) => {
    if (!window.confirm(
      `Delete the pre-joining record for ${p.name}?\n\n`
      + 'This removes the offer and joining trail for this candidate and cannot be undone. '
      + 'It does not touch an employee record already created from it.')) return;
    try {
      await preJoinerApi.remove(p.id);
      toast.success(`${p.name} removed from Pre-joining`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete this record');
    }
  };

  const hired = rows.filter((r) => r.status === 'Hired');
  const refused = rows.filter((r) => r.status === 'Refused');
  const joined = rows.filter((r) => r.status === 'Joined');

  const renderOffer = async (p, tok) => {
    try {
      const lr = await preJoinerApi.offerLetter(p.id, { logo_url: LOGO_URL, sign_token: tok || undefined });
      const w = window.open('', '_blank');
      if (w) { w.document.write(lr.data.html); w.document.close(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate offer letter'); }
  };

  const openOffer = async (p) => {
    // The stored signature is applied automatically. Set
    // SIGNATURE_REQUIRE_APPROVAL=true on the backend to reinstate the
    // signatory's emailed approval step.
    await renderOffer(p, '');
  };


  const doRefuse = async () => {
    if (!reason.trim()) { toast.error('Please enter a reason'); return; }
    setBusy(true);
    try { await preJoinerApi.refuse(refuseFor.id, reason.trim()); toast.success('Marked as refused to join'); setRefuseFor(null); setReason(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const mandatory = reqs.filter((r) => r.mandatory);
  const missing = mandatory.filter((r) => !files[r.name]);

  const doJoin = async () => {
    if (missing.length) { toast.error(`Upload required: ${missing.map((r) => r.name).join(', ')}`); return; }
    setBusy(true);
    try {
      const res = await preJoinerApi.join(joinFor.id);
      const id = res?.data?.employee_id;
      const chosen = Object.entries(files).filter(([, f]) => f);
      let failed = [];
      for (const [name, f] of chosen) { try { await documentApi.upload(id, name, f); } catch (e2) { failed.push(name); } }
      if (failed.length) toast.error(`Joined, but uploads failed: ${failed.join(', ')} — finish on the profile.`);
      else toast.success(`${joinFor.name} joined and moved to active employees`);
      setJoinFor(null); setFiles({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Join failed'); }
    finally { setBusy(false); }
  };

  const kpi = (label, val, tone) => (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-4">
      <p className="text-xs text-[#5A6377]">{label}</p>
      <p className={`text-2xl font-bold ${tone || 'text-[#11182B]'}`}>{val}</p>
    </div>
  );

  return (
    <>
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <h1 className="text-2xl font-semibold tracking-tight text-[#11182B] mb-1">Pre-joining / Offers</h1>
          <p className="text-sm text-[#5A6377] mb-6">Candidates hired but not yet joined{activeUnit ? ` · ${activeUnit}` : ''}. They are not counted in headcount, salary or KPIs until they join.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {kpi('Open offers', hired.length)}
            {kpi('Joined', joined.length, 'text-[#15A36E]')}
            {kpi('Refused to join', refused.length, 'text-[#C2151F]')}
            {kpi('Total tracked', rows.length)}
          </div>

          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5 mb-6">
            <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554] mb-3">Open offers</h2>
            {hired.length === 0 ? <p className="text-[#98A1B2] py-6 text-center">No open offers.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                    <tr><th className="py-2 px-3 text-left">Name</th><th className="py-2 px-3 text-left">Designation</th><th className="py-2 px-3 text-left">Dept</th><th className="py-2 px-3 text-left">DOJ</th><th className="py-2 px-3 text-right">Salary</th><th className="py-2 px-3 text-left">Reports To</th><th className="py-2 px-3"></th></tr>
                  </thead>
                  <tbody>
                    {hired.map((p) => (
                      <tr key={p.id} className="border-b border-[#F2F4F8]">
                        <td className="py-2 px-3 text-[#11182B] font-medium">{p.name}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.designation}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.department}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.doj}</td>
                        <td className="py-2 px-3 text-right">{Number(p.joining_salary || 0).toLocaleString('en-IN')}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.reporting_to_name || '—'}</td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          <button onClick={() => openOffer(p)} className="text-xs text-[#2563EB] mr-3 hover:underline">Offer Letter</button>
                          <button onClick={() => { setJoinFor(p); setFiles({}); }} className="text-xs text-[#15A36E] mr-3 hover:underline">Mark Joined</button>
                          <button onClick={() => { setRefuseFor(p); setReason(''); }} className="text-xs text-[#C2151F] hover:underline">Refused</button>
                          {isSuperAdmin && (
                            <button onClick={() => removeEntry(p)} className="text-xs text-[#5A6377] ml-3 hover:underline">Delete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {refused.length > 0 && (
            <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
              <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554] mb-3">Refused to join ({refused.length})</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                    <tr><th className="py-2 px-3 text-left">Name</th><th className="py-2 px-3 text-left">Designation</th><th className="py-2 px-3 text-left">Dept</th><th className="py-2 px-3 text-left">Reason</th><th className="py-2 px-3"></th></tr>
                  </thead>
                  <tbody>
                    {refused.map((p) => (
                      <tr key={p.id} className="border-b border-[#F2F4F8]">
                        <td className="py-2 px-3 text-[#11182B]">{p.name}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.designation}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.department}</td>
                        <td className="py-2 px-3 text-[#5A6377]">{p.refuse_reason}</td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {isSuperAdmin && (
                            <button onClick={() => removeEntry(p)} className="text-xs text-[#5A6377] hover:underline">Delete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Refuse dialog */}
      <Dialog open={!!refuseFor} onOpenChange={(o) => { if (!o) setRefuseFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Refused to join — {refuseFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5A6377] mb-2">Record why this candidate did not join (kept for analytics).</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Accepted another offer / salary mismatch / relocation"
            className="w-full border border-[#E6E9F0] rounded-lg p-2 text-sm" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setRefuseFor(null)}>Cancel</Button>
            <Button onClick={doRefuse} disabled={busy} className="bg-[#C2151F] hover:bg-[#A01019] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark-joined with mandatory documents */}
      <Dialog open={!!joinFor} onOpenChange={(o) => { if (!o) setJoinFor(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Mark joined — {joinFor?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5A6377] mb-2">Attach the mandatory joining documents (marked <span className="text-[#C2151F]">*</span>) to complete joining. Others can follow on the profile.</p>
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            {reqs.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-sm py-1 border-b border-[#F2F4F8] last:border-0">
                <span className="flex-1 text-[#11182B]">{r.name}{r.mandatory ? <span className="text-[#C2151F]"> *</span> : null}</span>
                {files[r.name] ? <span className="text-[11px] text-[#15A36E] truncate max-w-[130px]">{files[r.name].name}</span> : null}
                <label className="text-xs text-[#2563EB] cursor-pointer hover:underline">
                  {files[r.name] ? 'Change' : 'Choose file'}
                  <input type="file" className="hidden" onChange={(ev) => { const f = ev.target.files && ev.target.files[0]; setFiles((s) => ({ ...s, [r.name]: f || undefined })); }} />
                </label>
              </div>
            ))}
          </div>
          {missing.length > 0 && <p className="text-xs text-[#C2151F] mt-2">Still required: {missing.map((r) => r.name).join(', ')}</p>}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setJoinFor(null)}>Cancel</Button>
            <Button onClick={doJoin} disabled={busy || missing.length > 0} className="bg-[#15A36E] hover:bg-[#0E7D54] text-white disabled:opacity-50">Confirm Join</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    {signAsk && (
      <SignatureApprovalDialog
        sentTo={signAsk.sentTo}
        onClose={() => setSignAsk(null)}
        onSkip={() => { const p = signAsk.pending; setSignAsk(null); renderOffer(p, ''); }}
        onDone={(tok) => { signTokenRef.current = tok; const p = signAsk.pending; setSignAsk(null); renderOffer(p, tok); }}
      />
    )}
    </>
  );
}
